var m = require('mithril');
var prop = require('mithril/stream');
var modal = require('../../layout/components/modal');
var button = require('../../components/button');
var loadingModal = require('../../layout/components/loadingModal');
var loaders = require('../../layout/components/loaders');
var equipmentModel = require('../../equipment/models/equipment');
var fileInput = require('../../components/inputs/fileInput');
var ToggleSwitch = require('../../components/toggleSwitch');
var helpModal = require('../../help/helpModal');
var templateInputModeTabs = require('../../binding/components/templateInputModeTabs');
var bulkBindingFieldHelp = require('../../binding/components/bulkBindingFieldHelp');
var treeDevice = require('../../network/components/deviceTree');

var bulkCreateEquipmentModal = {};
var BULK_CREATE_EQUIPMENT_HELP_FILE = 'bulkCreateEquipment.md';

function extractRequestErrorMessage(error) {
  if (!error) return '';
  if (typeof error === 'string') return error;
  if (error.error && typeof error.error === 'string') return error.error;
  if (error.message && typeof error.message === 'string') return error.message;
  if (error.response) return extractRequestErrorMessage(error.response);
  return '';
}

function getVisibleAutoCreateKeys(locationClass) {
  if (locationClass === 'Level') return ['room'];
  if (locationClass === 'Building') return ['level', 'room'];
  if (locationClass === 'Site') return ['building', 'level', 'room'];
  if (locationClass === 'Room') return [];
  return ['site', 'building', 'level', 'room'];
}

function getVisibleAutoCreateKeySet(locationClass) {
  return new Set(getVisibleAutoCreateKeys(locationClass));
}

function normalizeRid(value) {
  var text = value === undefined || value === null ? '' : String(value).trim();
  if (!text) return '';
  return text.charAt(0) === '#' ? text : ('#' + text);
}

function isRidFormat(value) {
  return /^#-?\d+:\d+$/.test(String(value || '').trim());
}

function normalizeBacnetServerRecord(record) {
  if (!record || typeof record !== 'object') return null;
  var rid = normalizeRid(record['@rid'] || record.rid);
  if (!rid) return null;
  return {
    rid: rid,
    name: record.name ? String(record.name).trim() : '',
    host: record.host ? String(record.host).trim() : ''
  };
}

function formatBacnetServerLabel(record) {
  var normalized = normalizeBacnetServerRecord(record);
  if (!normalized) return '';
  var name = normalized.name || '(unnamed)';
  var hostSuffix = normalized.host ? (' • ' + normalized.host) : '';
  return name + hostSuffix + ' (' + normalized.rid + ')';
}

function resolveBacnetServerByRid(rawRid) {
  var rid = normalizeRid(rawRid);
  if (!rid) return Promise.resolve({ server: null, error: '' });
  if (!isRidFormat(rid)) {
    return Promise.resolve({ server: null, error: 'Invalid RID format. Use #12:0.' });
  }

  return loaders.requestWithLoader({
    loader: 'background',
    method: 'GET',
    url: '/api/v1/bacnetserver/' + encodeURIComponent(rid.replace(/^#/, ''))
  })
    .then(function (response) {
      var server = normalizeBacnetServerRecord(response);
      if (!server) {
        return {
          server: null,
          error: 'RID not found or it is not a BACnet Host.'
        };
      }
      return { server: server, error: '' };
    })
    .catch(function () {
      return {
        server: null,
        error: 'RID not found or it is not a BACnet Host.'
      };
    });
}

function collectBindingTemplateNames(classTree) {
  var templateClassesMap = new Map();

  var walk = function (node) {
    if (!node || typeof node !== 'object') return;
    var className = node.name ? String(node.name).trim() : '';
    var templates = node.data && Array.isArray(node.data.templates) ? node.data.templates : [];
    templates.forEach(function (template) {
      var templateName = template && typeof template.name === 'string' ? template.name.trim() : '';
      if (!templateName) return;
      if (!templateClassesMap.has(templateName)) {
        templateClassesMap.set(templateName, new Set());
      }
      if (className) {
        templateClassesMap.get(templateName).add(className);
      }
    });
    var children = Array.isArray(node.children) ? node.children : [];
    children.forEach(walk);
  };

  var categories = classTree && Array.isArray(classTree.categories) ? classTree.categories : [];
  categories.forEach(walk);
  return Array.from(templateClassesMap.entries())
    .map(function (entry) {
      var templateName = entry[0];
      var classNames = Array.from(entry[1] || []).sort(function (a, b) { return a.localeCompare(b); });
      var classSuffix = classNames.length ? (' (' + classNames.join(', ') + ')') : '';
      return {
        value: templateName,
        label: templateName + classSuffix
      };
    })
    .sort(function (a, b) {
      return a.label.localeCompare(b.label);
    });
}

function openHelpAndReturn(helpTitle, helpFileName, reopenFn) {
  if (typeof reopenFn !== 'function') return;
  var reopenOnHelpClose = function () {
    loadingModal.off('closed', reopenOnHelpClose);
    reopenFn();
    m.redraw();
  };

  loadingModal.on('closed', reopenOnHelpClose);
  modal.vm.close();
  Promise.resolve(helpModal.editDialogue(helpTitle || 'Bulk Create Equipment Help', helpFileName))
    .catch(function () {
      loadingModal.off('closed', reopenOnHelpClose);
      reopenFn();
    });
}

function openMarkdownHelpAndReturn(helpTitle, markdownText, reopenFn) {
  if (typeof reopenFn !== 'function') return;
  var reopenOnHelpClose = function () {
    loadingModal.off('closed', reopenOnHelpClose);
    reopenFn();
    m.redraw();
  };

  loadingModal.on('closed', reopenOnHelpClose);
  modal.vm.close();
  Promise.resolve(helpModal.editDialogue(helpTitle || 'Help', undefined, markdownText || 'No additional help available.'))
    .catch(function () {
      loadingModal.off('closed', reopenOnHelpClose);
      reopenFn();
    });
}

function renderModalTitleWithHelp(titleText, helpTitle, helpFileName, reopenFn) {
  return m('span.flex.items-center', [
    m('span', titleText || ''),
    m('i.ion-ios-help-outline.white.f3.ml2.pointer', {
      title: 'Open help',
      onclick: function (event) {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        openHelpAndReturn(helpTitle, helpFileName, reopenFn);
      }
    })
  ]);
}

function renderSelectedBacnetServerInfo(serverRecord) {
  var server = normalizeBacnetServerRecord(serverRecord);
  if (!server) {
    return m('span.gray', 'Selected BACnet Host details will appear here');
  }

  var ridWithoutHash = server.rid.replace(/^#/, '');

  return m('div.f6.gray', [
    m('a.link.blue.pointer.mr2', {
      href: '/#!/bacnetserver/' + encodeURIComponent(ridWithoutHash),
      target: '_blank',
      rel: 'noopener noreferrer'
    }, server.name || '(unnamed)'),
    m('span', ' - '),
    m('span', '(' + (server.host || 'no host') + ')')
  ]);
}

bulkCreateEquipmentModal.open = function (options) {
  var params = options && typeof options === 'object' ? options : {};
  var locationRecord = params.locationRecord || null;
  var onCreated = typeof params.onCreated === 'function' ? params.onCreated : function () { return Promise.resolve(); };
  var currentLocationClass = String((locationRecord && locationRecord['@class']) || '');
  var locationRid = locationRecord && locationRecord['@rid'] ? String(locationRecord['@rid']) : '';
  var locationRidParam = locationRid.replace(/^#/, '');

  var selectedFile = prop(null);
  var selectedFileName = prop('');
  var errorMessage = prop('');
  var submitting = prop(false);
  var resultSummary = prop(null);
  var autoCreateSite = prop(false);
  var autoCreateBuilding = prop(false);
  var autoCreateLevel = prop(false);
  var autoCreateRoom = prop(true);
  var applyBindingAfterCreate = prop(false);
  var bindingTemplate = prop('');
  var bindingInputMode = prop('template');
  var bindingTemplateContent = prop('');
  var bindingTemplateFileName = prop('');
  var bindingSearchPattern = prop('');
  var bindingRestrictToServer = prop(false);
  var bindingSelectedServerRid = prop('');
  var bindingSelectedServerRecord = prop(null);
  var bindingTemplateOptions = prop([]);
  var loadingBindingTemplateOptions = prop(false);
  var bindingTemplateOptionsError = prop('');
  var bindingSelectedServerName = prop('');
  var bindingServerPickerOpen = prop(false);
  var bindingServerSelectedNode = prop(null);
  var bindingServerResolving = prop(false);
  var bindingServerError = prop('');
  var bindingServerLookupRequestId = 0;

  var openBindingFieldHelp = function (fieldKey, fallbackTitle) {
    var resolvedHelp = bulkBindingFieldHelp.getBulkBindingFieldHelp(fieldKey);
    var title = (resolvedHelp && resolvedHelp.title) || fallbackTitle || 'Binding Field Help';
    var markdownText = (resolvedHelp && resolvedHelp.markdown) || 'No additional help available for this field.';
    openMarkdownHelpAndReturn(title, markdownText, openModal);
  };

  var renderBindingHelpIcon = function (fieldKey, fallbackTitle) {
    return m('i.ion-ios-help-outline.white.f3.ml2.pointer', {
      title: 'Help',
      role: 'button',
      tabindex: '0',
      'aria-label': 'Help for ' + (fallbackTitle || fieldKey || 'field'),
      onclick: function (event) {
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
        openBindingFieldHelp(fieldKey, fallbackTitle);
      },
      onkeypress: function (event) {
        if (event && (event.key === 'Enter' || event.key === ' ')) {
          event.preventDefault();
          openBindingFieldHelp(fieldKey, fallbackTitle);
        }
      }
    });
  };

  var buildTemplateHref = function () {
    return '/api/v1/location/equipment/bulk-create/template?scope=' + encodeURIComponent(currentLocationClass.toLowerCase());
  };

  var loadBindingTemplateOptions = function () {
    if (loadingBindingTemplateOptions()) return;
    loadingBindingTemplateOptions(true);
    bindingTemplateOptionsError('');
    equipmentModel.getClassTree()
      .then(function (classTree) {
        bindingTemplateOptions(collectBindingTemplateNames(classTree));
      })
      .catch(function () {
        bindingTemplateOptions([]);
        bindingTemplateOptionsError('Unable to load template names.');
      })
      .finally(function () {
        loadingBindingTemplateOptions(false);
        m.redraw();
      });
  };

  var applySelectedBindingServer = function (record, options) {
    var opts = options && typeof options === 'object' ? options : {};
    var server = normalizeBacnetServerRecord(record);
    if (!server) {
      bindingSelectedServerRid('');
      bindingSelectedServerName('');
      bindingSelectedServerRecord(null);
      bindingServerError('');
      if (opts.clearPickerNode === true) {
        bindingServerSelectedNode(null);
      }
      return;
    }
  
    bindingSelectedServerRid(server.rid);
    bindingSelectedServerName(server.name || '');
    bindingSelectedServerRecord(server);
    bindingServerError('');
  };

  var resolveSelectedBindingServerRid = function (rawRid) {
    var normalized = normalizeRid(rawRid);
    bindingSelectedServerRid(normalized);
    bindingSelectedServerName('');
    bindingServerError('');
    if (!normalized) {
      bindingServerSelectedNode(null);
      bindingSelectedServerRecord(null);
      return Promise.resolve();
    }

    bindingServerLookupRequestId += 1;
    var requestId = bindingServerLookupRequestId;
    bindingServerResolving(true);
    m.redraw();

    return resolveBacnetServerByRid(normalized)
      .then(function (result) {
        if (requestId !== bindingServerLookupRequestId) return;
        if (result && result.server) {
          applySelectedBindingServer(result.server, { clearPickerNode: true });
          return;
        }
        bindingSelectedServerName('');
        bindingSelectedServerRecord(null);
        bindingServerError((result && result.error) ? result.error : 'Unable to resolve BACnet Host.');
      })
      .finally(function () {
        if (requestId !== bindingServerLookupRequestId) return;
        bindingServerResolving(false);
        m.redraw();
      });
  };

  var setSelectedFile = function (file) {
    selectedFile(file || null);
    selectedFileName(file && file.name ? file.name : '');
    errorMessage('');
    resultSummary(null);
  };

  var setBindingInputMode = function (mode) {
    bindingInputMode(mode);
    errorMessage('');
    resultSummary(null);
    if (mode !== 'upload') {
      bindingTemplateFileName('');
    }
    if (mode !== 'paste' && mode !== 'upload') {
      bindingTemplateContent('');
      bindingTemplateFileName('');
    }
  };

  var setBindingSearchMode = function (mode) {
    var nextRestrictToServer = mode === 'device-name';
    bindingRestrictToServer(nextRestrictToServer);
    if (nextRestrictToServer) {
      bindingServerPickerOpen(true);
      bindingServerError('');
    } else {
      bindingServerPickerOpen(false);
    }
  };

  var setBindingTemplateFile = function (file) {
    if (!file) {
      bindingTemplateFileName('');
      bindingTemplateContent('');
      return;
    }
    var reader = new FileReader();
    reader.onload = function (event) {
      bindingTemplateFileName(file.name || '');
      bindingTemplateContent((event && event.target && event.target.result) ? event.target.result : '');
      m.redraw();
    };
    reader.readAsText(file);
  };

  var submit = async function () {
    if (submitting()) return;

    if (!selectedFile()) {
      errorMessage('Please select an Excel (.xlsx) file.');
      m.redraw();
      return;
    }
    if (!locationRidParam) {
      errorMessage('Unable to determine target location.');
      m.redraw();
      return;
    }

    var selectedBindingMode = String(bindingInputMode() || 'template').trim();
    var selectedBindingTemplate = selectedBindingMode === 'template' ? String(bindingTemplate() || '').trim() : '';
    var selectedBindingTemplateContent = selectedBindingMode !== 'template' ? String(bindingTemplateContent() || '').trim() : '';
    if (applyBindingAfterCreate()) {
      if (selectedBindingMode === 'template' && !selectedBindingTemplate) {
        errorMessage('Binding Template is required when applying bindings.');
        m.redraw();
        return;
      }
      if (selectedBindingMode !== 'template' && !selectedBindingTemplateContent) {
        errorMessage('Template JSON Content is required when applying bindings.');
        m.redraw();
        return;
      }
      if (bindingRestrictToServer() === true && !normalizeRid(bindingSelectedServerRid())) {
        errorMessage('Select a BACnet Host when Search for is set to Device Name.');
        m.redraw();
        return;
      }
      if (bindingRestrictToServer() === true && !isRidFormat(normalizeRid(bindingSelectedServerRid()))) {
        errorMessage('BACnet Host RID must use format #12:0 when Search for is set to Device Name.');
        m.redraw();
        return;
      }
    }

    errorMessage('');
    submitting(true);
    m.redraw();

    try {
      var formData = new FormData();
      formData.append('file', selectedFile());
      var visibleOptions = getVisibleAutoCreateKeySet(currentLocationClass);
      formData.append('autoCreateSite', String(visibleOptions.has('site') && autoCreateSite() === true));
      formData.append('autoCreateBuilding', String(visibleOptions.has('building') && autoCreateBuilding() === true));
      formData.append('autoCreateLevel', String(visibleOptions.has('level') && autoCreateLevel() === true));
      formData.append('autoCreateRoom', String(visibleOptions.has('room') && autoCreateRoom() === true));

      var response = await loaders.requestWithLoader({
        loader: 'main',
        method: 'POST',
        url: '/api/v1/location/' + locationRidParam + '/equipment/bulk-create',
        body: formData
      });

      var combinedSummary = Object.assign({}, response || {});
      resultSummary(combinedSummary);
      if (response && response.createdCount > 0) {
        await onCreated();
      }

      if (applyBindingAfterCreate() && response && response.createdCount > 0) {
        try {
          var bindingResponse = await loaders.requestWithLoader({
            loader: 'main',
            method: 'POST',
            url: '/api/v1/location/equipment/bulk-bind',
            body: {
              created: response.created || [],
              bindingTemplate: selectedBindingTemplate,
              templateContent: selectedBindingTemplateContent,
              searchPattern: String(bindingSearchPattern() || '').trim(),
              restrictToServer: bindingRestrictToServer() === true,
              selectedServerRid: bindingRestrictToServer() === true
                ? normalizeRid(bindingSelectedServerRid())
                : ''
            }
          });
          combinedSummary.binding = bindingResponse || null;
          resultSummary(combinedSummary);
        } catch (bindingError) {
          combinedSummary.binding = {
            total: (response && Array.isArray(response.created)) ? response.created.length : 0,
            success: false,
            exitCode: (bindingError && typeof bindingError.exitCode === 'number') ? bindingError.exitCode : 1,
            stdout: bindingError && bindingError.stdout ? bindingError.stdout : '',
            stderr: bindingError && bindingError.stderr ? bindingError.stderr : '',
            appliedCount: 0,
            failedCount: (response && Array.isArray(response.created)) ? response.created.length : 0,
            applied: [],
            failures: [{
              error: extractRequestErrorMessage(bindingError) || 'Bulk binding request failed.'
            }]
          };
          resultSummary(combinedSummary);
          errorMessage(extractRequestErrorMessage(bindingError) || 'Equipment was created, but bulk binding failed.');
        }
      }

      if (!applyBindingAfterCreate() && response && response.failedCount === 0) {
        modal.vm.close();
      }
    } catch (error) {
      errorMessage(extractRequestErrorMessage(error) || 'Unable to bulk create equipment.');
    } finally {
      submitting(false);
      m.redraw();
    }
  };

  var renderBody = function () {
    var summary = resultSummary();
    var failures = summary && Array.isArray(summary.failures) ? summary.failures : [];
    var visibleOptionKeys = getVisibleAutoCreateKeySet(currentLocationClass);
    var createOptions = [
      {
        key: 'site',
        label: 'Create missing sites automatically',
        checked: autoCreateSite() === true,
        onToggle: function () { autoCreateSite(!autoCreateSite()); }
      },
      {
        key: 'building',
        label: 'Create missing buildings automatically',
        checked: autoCreateBuilding() === true,
        onToggle: function () { autoCreateBuilding(!autoCreateBuilding()); }
      },
      {
        key: 'level',
        label: 'Create missing levels automatically',
        checked: autoCreateLevel() === true,
        onToggle: function () { autoCreateLevel(!autoCreateLevel()); }
      },
      {
        key: 'room',
        label: 'Create missing rooms automatically',
        checked: autoCreateRoom() === true,
        onToggle: function () { autoCreateRoom(!autoCreateRoom()); }
      }
    ].filter(function (option) {
      return visibleOptionKeys.has(option.key);
    });

    return m('form', { role: 'form' }, [
      m('div.f5.mb2', 'Upload an Excel file to create equipment in bulk.'),
      m('div.mb3', [
        m('a.pointer.ba.b--mid-gray.ph2.pv1.mr2.br2.near-white.link', {
          href: buildTemplateHref(),
          target: '_blank',
          rel: 'noopener noreferrer'
        }, 'Download Excel Template')
      ]),
      m('div.mb3', [
        m(fileInput, {
          field: {
            id: 'location-bulk-create-equipment-excel',
            name: 'Excel File (.xlsx)'
          },
          accept: '.xlsx',
          onChange: setSelectedFile
        }),
        selectedFileName()
          ? m('div.f6.gray.mt2', selectedFileName())
          : ''
      ]),
      createOptions.length > 0
        ? m('div.mb3.ba.b--moon-gray.br2.pa3', [
          m('div.f6.b.mb2', 'Missing location handling'),
          m('div.f6.gray.mb3', 'Choose what should be auto-created when a location in the spreadsheet does not exist.'),
          createOptions.map(function (option) {
            var toggleId = 'bulk-create-auto-' + option.key;
            return m('div.flex.items-center.justify-between.mb2', { key: option.key }, [
              m('label.f5', { for: toggleId }, option.label),
              m(ToggleSwitch, {
                id: toggleId,
                checked: option.checked,
                ariaLabel: option.label,
                onchange: option.onToggle
              })
            ]);
          })
        ])
        : '',
      m('div.mb3.ba.b--moon-gray.br2.pa3', [
        m('div.flex.items-center.justify-between.mb2', [
          m('div.flex.items-center', [
            m('div.f6.b', 'Binding (Optional)'),
            renderBindingHelpIcon('applyBinding', 'Binding (Optional)')
          ]),
          m(ToggleSwitch, {
            id: 'bulk-create-apply-binding-toggle',
            checked: applyBindingAfterCreate() === true,
            disabled: submitting(),
            ariaLabel: 'Apply bindings after creating equipment',
            onchange: function () {
              applyBindingAfterCreate(!applyBindingAfterCreate());
            }
          })
        ]),
        applyBindingAfterCreate()
          ? m('div', [
            m('div.ba.b--moon-gray.br2.pa3.mb3', [
              m('div.flex.items-center.justify-between.mb2', [
                m('span.f4', 'Set template'),
                m(templateInputModeTabs, {
                  className: 'flex items-center',
                  activeKey: bindingInputMode(),
                  onSelect: setBindingInputMode,
                  tabs: [
                    { key: 'template', label: 'Stored Templates' },
                    { key: 'paste', label: 'Paste JSON' },
                    { key: 'upload', label: 'Upload File' }
                  ]
                })
              ]),
              bindingInputMode() === 'template'
                ? m('div', [
                  m('div.flex.items-center.mb1', [
                    m('label.db.f5.mb0', { for: 'bulk-create-binding-template' }, 'Binding Template'),
                    renderBindingHelpIcon('bindingTemplate', 'Binding Template')
                  ]),
                  m('select#bulk-create-binding-template.w-100.ba.b--mid-gray.br2.pa2.mb2', {
                    value: bindingTemplate(),
                    disabled: loadingBindingTemplateOptions() || submitting(),
                    onchange: function (event) {
                      bindingTemplate(event && event.target ? event.target.value : '');
                    }
                  }, [
                    m('option', { value: '' }, loadingBindingTemplateOptions() ? 'Loading templates...' : 'Select template'),
                    (bindingTemplateOptions() || []).map(function (templateOption) {
                      var optionValue = templateOption && templateOption.value ? templateOption.value : '';
                      var optionLabel = templateOption && templateOption.label ? templateOption.label : optionValue;
                      return m('option', { value: optionValue, key: optionValue + '::' + optionLabel }, optionLabel);
                    })
                  ]),
                  loadingBindingTemplateOptions() ? m('div.f6.gray.mb2', 'Loading template suggestions...') : '',
                  bindingTemplateOptionsError() ? m('div.f6.red.mb2', bindingTemplateOptionsError()) : ''
                ])
                : '',
              bindingInputMode() === 'upload'
                ? m('div', [
                  m('div.flex.items-center.mb1', [
                    m('label.db.f5.mb0', { for: 'bulk-create-binding-template-upload' }, 'Template File *'),
                    renderBindingHelpIcon('templateContent', 'Template JSON Content')
                  ]),
                  m(fileInput, {
                    field: {
                      id: 'bulk-create-binding-template-upload',
                      name: 'Upload File',
                      accept: '.json,.txt'
                    },
                    onChange: setBindingTemplateFile
                  }),
                  m('div.f6.mid-gray.mb2', bindingTemplateFileName() || 'No file selected')
                ])
                : bindingInputMode() === 'paste'
                  ? m('div', [
                    m('div.flex.items-center.mb1', [
                      m('label.db.f5.mb0', { for: 'bulk-create-binding-template-content' }, 'Set Template'),
                      renderBindingHelpIcon('templateContent', 'Template JSON Content')
                    ]),
                    m('textarea#bulk-create-binding-template-content.w-100.ba.b--mid-gray.br2.pa2.mb2', {
                      value: bindingTemplateContent(),
                      placeholder: 'Paste binding template JSON here',
                      oninput: function (event) {
                        bindingTemplateContent(event && event.target ? event.target.value : '');
                      },
                      style: {
                        height: '35vh',
                        resize: 'vertical'
                      }
                    })
                  ])
                  : ''
            ]),
            m('div.ba.b--moon-gray.br2.pa3', [
              m('div.flex.items-center.justify-between.mb2', [
                m('div.flex.items-center', [
                  m('span.f4', 'Search for'),
                  renderBindingHelpIcon('restrictToServer', 'Search For')
                ]),
                m(templateInputModeTabs, {
                  className: 'flex items-center',
                  activeKey: bindingRestrictToServer() ? 'device-name' : 'server-name',
                  onSelect: setBindingSearchMode,
                  tabs: [
                    { key: 'server-name', label: 'Host Name' },
                    { key: 'device-name', label: 'Device Name' }
                  ]
                })
              ]),
              bindingRestrictToServer()
                ? m('div', [
                  m('div.flex.items-center.mb1', [
                    m('label.db.f5.mb0', { for: 'bulk-create-selected-server-rid' }, 'BACnet Host *'),
                    renderBindingHelpIcon('selectedServerRid', 'BACnet Host')
                  ]),
                  m('div.flex.items-start.mb2', [
                    m('input#bulk-create-selected-server-rid.ba.b--mid-gray.br2.pa2.mr2', {
                      type: 'text',
                      value: normalizeRid(bindingSelectedServerRid()),
                      style: {
                        width: '10rem',
                        minWidth: '10rem'
                      },
                      oninput: function (event) {
                        bindingSelectedServerRid(event && event.target ? event.target.value : '');
                        bindingSelectedServerName('');
                        bindingSelectedServerRecord(null);
                        bindingServerError('');
                      },
                      onblur: function (event) {
                        resolveSelectedBindingServerRid(event && event.target ? event.target.value : '');
                      }
                    }),
                    m('div.flex-auto.pt2', {
                      style: {
                        minHeight: '2.5rem',
                        wordBreak: 'break-word'
                      }
                    }, renderSelectedBacnetServerInfo(bindingSelectedServerRecord()))
                  ]),
                  m('div.flex.items-center.mb2', [
                    m('a.pointer.ba.b--mid-gray.ph2.pv1.br2.near-white.link.mr2', {
                      onclick: function (event) {
                        if (event) event.preventDefault();
                        var nextOpen = !bindingServerPickerOpen();
                        bindingServerPickerOpen(nextOpen);
                      }
                    }, bindingServerPickerOpen() ? 'Hide Host Browser' : 'Browse BACnet Hosts')
                  ]),
                  bindingServerResolving() ? m('div.f6.gray.mb2', 'Validating BACnet Host RID...') : '',
                  bindingServerError() ? m('div.f6.red.mb2', bindingServerError()) : '',
                  bindingServerPickerOpen()
                    ? m('div.ba.b--moon-gray.br2.pa2.mb2', [
                      m(treeDevice, {
                        selectedNode: bindingServerSelectedNode,
                        loader: 'background',
                        itemsPerPage: 15,
                        hideLabel: true,
                        selectableClasses: ['BacnetServer'],
                        selectOnLabelClick: true
                      }),
                      m('div.mt3.tr', [
                        m(button, {
                          label: 'Use Selected BACnet Host',
                          class: 'btn-good dib ph3 pv2 ' + ((bindingServerSelectedNode() && bindingServerSelectedNode()['@rid']) ? '' : 'o-50'),
                          func: function (event) {
                            if (event) event.preventDefault();
                            var selectedServer = bindingServerSelectedNode();
                            if (!selectedServer || !selectedServer['@rid']) return;
                            applySelectedBindingServer(selectedServer);
                            bindingServerPickerOpen(false);
                          }
                        })
                      ])
                    ])
                    : ''
                ])
                : '',
              m('div.flex.items-center.mb1', [
                m('label.db.f5.mb0', { for: 'bulk-create-search-pattern' },
                  bindingRestrictToServer() ? 'Device Name' : 'Host Name'
                ),
                renderBindingHelpIcon('searchPattern', 'Search Pattern')
              ]),
              m('input#bulk-create-search-pattern.w-100.ba.b--mid-gray.br2.pa2.mb2', {
                type: 'text',
                value: bindingSearchPattern(),
                placeholder: bindingRestrictToServer() ? 'AHU-01' : 'BACnet-Host-01',
                oninput: function (event) {
                  bindingSearchPattern(event && event.target ? event.target.value : '');
                }
              }),
              bindingRestrictToServer()
                ? m('div.f6.gray.mb2', [
                    'This search pattern is used to find a device by the equipment name. Leave it blank to use the new equipment name. Or enter a string and use ',
                    m('code', '{Name}'),
                    ' to inject the equipment name. e.g. ',
                    m('code', 'Example Building - Level 4 - {Name}'),
                    ' searches for a device named ',
                    m('code', 'Example Building - Level 4 - AHU-3')
                  ])
                : m('div.f6.gray.mb2', [
                    'This search pattern is used to find a BACnet Host by the equipment name. Leave it blank to use the new equipment name. Or enter a string and use ',
                    m('code', '{Name}'),
                    ' to inject the equipment name. e.g. ',
                    m('code', 'Example Building - Level 4 - {Name}'),
                    ' searches for a BACnet Host named ',
                    m('code', 'Example Building - Level 4 - AHU-3')
                  ])
            ])
          ])
          : ''
      ]),
      errorMessage()
        ? m('div.red.f6.mb2', errorMessage())
        : '',
      summary
        ? m('div.mt3', [
          m('div.f5.mb2', 'Processed ' + summary.totalRows + ' row(s): ' + summary.createdCount + ' created, ' + summary.failedCount + ' failed.'),
          failures.length > 0
            ? m('div', [
              m('div.f6.b.mb1', 'Failed rows'),
              m('ul.pl3.mt1', failures.slice(0, 20).map(function (failure, idx) {
                return m('li.mb1', {
                  key: failure.row || idx
                }, 'Row ' + failure.row + ': ' + (failure.error || 'Unknown error'));
              })),
              failures.length > 20
                ? m('div.f6.gray', 'Showing first 20 failures.')
                : ''
            ])
            : '',
          (function () {
            var bindingSummary = summary && summary.binding ? summary.binding : null;
            if (!bindingSummary) return '';
            var bindingFailures = Array.isArray(bindingSummary.failures) ? bindingSummary.failures : [];
            return m('div.mt3', [
              m('div.f5.mb2', 'Binding: ' + bindingSummary.appliedCount + ' applied, ' + bindingSummary.failedCount + ' failed.'),
              m('div.f6.gray.mb2', 'Exit code: ' + (typeof bindingSummary.exitCode === 'number' ? bindingSummary.exitCode : 'n/a')),
              bindingFailures.length > 0
                ? m('div', [
                  m('div.f6.b.mb1', 'Binding failures'),
                  m('ul.pl3.mt1', bindingFailures.slice(0, 20).map(function (failure, idx) {
                    var failureLabel = failure && failure.rid ? failure.rid : ('item ' + (idx + 1));
                    return m('li.mb1', {
                      key: failure.row || failure.rid || idx
                    }, failureLabel + ': ' + (failure.error || 'Unknown error'));
                  })),
                  bindingFailures.length > 20
                    ? m('div.f6.gray', 'Showing first 20 binding failures.')
                    : ''
                ])
                : m('div.f6.gray.mb2', 'No binding failures.'),
              m('div.flex.flex-column.flex-row-ns.mt2', [
                m('div.w-100.w-50-ns.pr0.pr3-ns', [
                  m('div.f6.b.mb1', 'STDOUT'),
                  m('pre.code.ba.b--mid-gray.pa2', {
                    style: { maxHeight: '20rem', overflow: 'auto' }
                  }, bindingSummary.stdout ? bindingSummary.stdout : '(no output)')
                ]),
                m('div.w-100.w-50-ns.pl0.pl3-ns.mt3.mt0-ns', [
                  m('div.f6.b.mb1', 'STDERR'),
                  m('pre.code.ba.b--mid-gray.pa2', {
                    style: { maxHeight: '20rem', overflow: 'auto' }
                  }, bindingSummary.stderr ? bindingSummary.stderr : '(no output)')
                ])
              ])
            ]);
          })()
        ])
        : '',
      submitting()
        ? m('div.gray.f6.mt2', 'Processing file...')
        : ''
    ]);
  };

  var openModal = function () {
    modal.vm.title(
      renderModalTitleWithHelp(
        'Bulk Create Equipment',
        'Bulk Create Equipment Help',
        BULK_CREATE_EQUIPMENT_HELP_FILE,
        openModal
      )
    );
    modal.vm.body = renderBody;
    modal.vm.buttons([
      {
        label: 'Upload and Create',
        class: 'btn-good dib mr2',
        func: submit
      },
      {
        label: 'Close',
        class: 'dib',
        func: function () {
          if (submitting()) return Promise.resolve();
          modal.vm.close();
        }
      }
    ]);
    modal.vm.open({ extrawidth: true });
  };

  loadBindingTemplateOptions();
  openModal();
};

module.exports = bulkCreateEquipmentModal;
